// Audience UI Application.
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

/**
 * Render a row in the stock ticker.
 *
 * @param {object} props Stock props.
 * @param {integer} props.key Component index.
 * @param {string} props.image Symbol image URL.
 * @param {string} props.name Symbol name.
 * @param {Number} props.price Current stock price.
 * @param {string} props.currency Currency symbol.
 * @param {Number} props.change Change in stock price.
 * @return {React.Component}
 */
const Stock = ( { key, image, name, price, currency, change } ) => (
	<tr key={ key }>
		<td className='st-field'>
			<span className="st-field__logo">
				<img src={ image } />
			</span>
			<span className='st-field__name'>{ name }</span>
		</td>
		<td>{price} {currency}</td>
		<td class={ change < 0 ? 'negative' : 'positive'}><i className={ 'fa ' + ( change < 0 ? 'fa-caret-down negative' : 'fa-caret-up positive' )} /> { change }%</td>
	</tr>
);

/**
 * Render the stock ticker.
 *
 * @return {React.Component}
 */
const StockTicker = () => {
	const [stocks, setStocks] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [sorting, setSorting] = useState({key: 'name', ascending: true});
	const [selectedMarket, setSelectedMarket] = useState('');
	const { rnStockTickerData } = window;

	// Fetch new ticker data when selected region is updated.
	useEffect(() => {
		if ( selectedMarket ) {
			setIsLoading(true);
			getStockTickers();
		}
	}, [selectedMarket]);

	// Initialize ticker with user region.
	useEffect(() => {
		setSelectedMarket( rnStockTickerData.default );
	}, [] );

	function getStockTickers() {
		if ( selectedMarket in rnStockTickerData ) {
			processTickerData( rnStockTickerData[ selectedMarket ] );
		} else {
			fetch(`/wp-json/react-news/stock-ticker/${selectedMarket}`)
				.then((resp) => resp.json())
				.then((data) => {
					rnStockTickerData[ selectedMarket ] = data;
					processTickerData( data );
				});
		}
	}

	function processTickerData( data ) {
		let tickers = [];
		data.sort(function (a, b) {
			var nameA = a.name.toUpperCase();
			var nameB = b.name.toUpperCase();
			if (nameA < nameB) {
				return -1;
			}
			if (nameA > nameB) {
				return 1;
			}
			return 0;
		});
		data.map(function(tick) {
			tickers.push(tick);
		});

		setStocks(tickers);
		setIsLoading(false);
	}

	function applySorting(key, ascending) {
		setSorting({ key: key, ascending: ascending });
	}

	useEffect(() => {
    // Copy array to prevent data mutation
		const stocksCopy = [...stocks];

		// Apply sorting
		const sortedStocks = stocksCopy.sort((a, b) => {
			if(sorting.key === 'change' || sorting.key === 'price') {
				if (sorting.key === 'change') {
					a[ sorting.key ] = a[ sorting.key ].replace(',', '');
					b[ sorting.key ] = b[ sorting.key ].replace(',', '');
				}
				return Number( a[ sorting.key ] ) - Number( b[sorting.key] );
			}
			return a[sorting.key].localeCompare(b[sorting.key]);
		});

		// Replace currentUsers with sorted currentUsers
		setStocks(
			// Decide either currentUsers sorted by ascending or descending order
			sorting.ascending ? sortedStocks : sortedStocks.reverse()
		);
	}, [sorting]);

	return (
	<div class="rn-stock-ticker">
		<div class="tabs__container">
			<span class={'tabs__header ' + (selectedMarket == 'uk' ? 'active' : '') } onClick={() => setSelectedMarket('uk')}>
				<span className="tabs__header-full-name">United Kingdom</span> <span className="tabs__header-abbreviation">UK</span></span>
			<span class={'tabs__header ' + (selectedMarket == 'europe' ? 'active' : '') } onClick={() => setSelectedMarket('europe')}>
				Europe</span>
			{rnStockTickerData.hasUS && (
				<span class={'tabs__header ' + (selectedMarket == 'us' ? 'active' : '') } onClick={() => setSelectedMarket('us')}>
					<span className="tabs__header-full-name">United States</span> <span className="tabs__header-abbreviation">US</span></span>
			)}
		</div>
		{isLoading ? <div>We are loading the stock ticker</div> :
			<table class="rn-stock-ticker__table" cellspacing="0">
				<thead>
					<tr>
						<td onClick={() => applySorting('name', !sorting.ascending)}>
							Company {sorting.key === 'name' && <span className="rn-stock-ticker__sort-indicator">{sorting.ascending ? '▲' : '▼'}</span>}
						</td>
						<td onClick={() => applySorting('price', !sorting.ascending)}>
							Price {sorting.key === 'price' && <span className="rn-stock-ticker__sort-indicator">{sorting.ascending ? '▲' : '▼'}</span>}
						</td>
						<td onClick={() => applySorting('change', !sorting.ascending)}>
							% change {sorting.key === 'change' && <span className="rn-stock-ticker__sort-indicator">{sorting.ascending ? '▲' : '▼'}</span>}
						</td>
					</tr>
				</thead>
				<tbody>
					{
						stocks.map((stock, i) => {
							const currency = (selectedMarket === 'europe' || selectedMarket === 'us' ? stock.currency : '');
							let price = parseFloat(stock.price);

							if (selectedMarket === 'uk') {
								price = (price * 100);
							}

							price = price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

							return <Stock key={ i } { ...stock } />;
						})
					}
				</tbody>
			</table>
		}
	</div>
	);
};

document.addEventListener( 'DOMContentLoaded', () => {
	// Get the Stock Ticker placeholder.
	const stEl = document.getElementById( 'stock-ticker' );

	// Is our audience UI placeholder present?
	if ( stEl ) {
		// Mount audience react app.
		ReactDOM.render(
			<StockTicker />,
			stEl
		);
	}
} );
